import type { Op } from "sharedb/lib/client";

import type { ExtendedDoc, ExtendedOp, PathType, WorksheetType } from "../shared/types/worksheet";
import {
  fieldContentLogIds,
  fieldPathKey,
  getAffectedAnswerFieldPaths,
  isSignificantFieldTextLoss,
  opsTouchAnswerData,
  plainTextAtPath,
  plainTextsForFieldPaths,
} from "../shared/worksheets/worksheet-field-content-utils";

import { logInfoWithDebugFlag, rollbarAndLogWarning } from "utils/logger";
import { WaffleFlags } from "utils/waffle-flags";

export type ClientWorksheetFieldContentLossCause = "rollback" | "submit-error" | "timeout";

type LocalOpFieldTextTracker = {
  docId: string;
  fieldPaths: PathType[];
  beforeSubmit: Record<string, string>;
  afterLocalApply: Record<string, string> | null;
};

const localOpFieldTextTrackers = new Map<string, LocalOpFieldTextTracker>();

const getOperationIdFromOps = (ops: ExtendedOp[]): string | undefined => {
  for (const op of ops) {
    if (op.operationId) {
      return op.operationId;
    }
    if (op.op) {
      const nestedId = op.op.find((nestedOp) => nestedOp.operationId)?.operationId;
      if (nestedId) {
        return nestedId;
      }
    }
  }
  return undefined;
};

const flattenOps = (ops: ExtendedOp[]): Op[] => ops.flatMap((op) => (op.op ? op.op : [op]));

/** Capture field plain text before a locally submitted op is applied. */
export const trackLocalOpFieldTextBeforeApply = (
  docId: string,
  doc: WorksheetType,
  batchOps: ExtendedOp[],
): void => {
  const operationId = getOperationIdFromOps(batchOps);
  if (!operationId) {
    return;
  }

  const ops = flattenOps(batchOps);
  if (!opsTouchAnswerData(ops)) {
    return;
  }

  const fieldPaths = getAffectedAnswerFieldPaths(ops);
  if (fieldPaths.length === 0) {
    return;
  }

  localOpFieldTextTrackers.set(operationId, {
    docId,
    fieldPaths,
    beforeSubmit: plainTextsForFieldPaths(doc, fieldPaths),
    afterLocalApply: null,
  });
};

/** Capture field plain text after a locally submitted op is applied. */
export const trackLocalOpFieldTextAfterApply = (
  doc: WorksheetType,
  batchOps: ExtendedOp[],
): void => {
  const operationId = getOperationIdFromOps(batchOps);
  if (!operationId) {
    return;
  }

  const tracker = localOpFieldTextTrackers.get(operationId);
  if (!tracker) {
    return;
  }

  const ops = flattenOps(batchOps);
  const fieldPaths = getAffectedAnswerFieldPaths(ops);
  tracker.afterLocalApply = plainTextsForFieldPaths(doc, fieldPaths);
};

export const clearLocalOpFieldTextTracker = (operationId: string): void => {
  localOpFieldTextTrackers.delete(operationId);
};

const reportFieldTextLossForOperationId = (params: {
  docId: string;
  doc: WorksheetType;
  operationId: string;
  cause: ClientWorksheetFieldContentLossCause;
  userId: string | null | undefined;
  sectionId: string | null | undefined;
  errorMessage?: string;
}): Record<string, unknown>[] => {
  const { docId, doc, operationId, cause, userId, sectionId, errorMessage } = params;

  const tracker = localOpFieldTextTrackers.get(operationId);
  localOpFieldTextTrackers.delete(operationId);
  if (!tracker || tracker.docId !== docId || !tracker.afterLocalApply) {
    return [];
  }

  const losses: Record<string, unknown>[] = [];

  for (const fieldPath of tracker.fieldPaths) {
    const key = fieldPathKey(fieldPath);
    const textAttempted = tracker.afterLocalApply[key] ?? "";
    const textNow = plainTextAtPath(doc, fieldPath);
    const textBeforeSubmit = tracker.beforeSubmit[key] ?? "";
    const charsLost = textAttempted.length - textNow.length;

    if (
      charsLost > 0 &&
      isSignificantFieldTextLoss(charsLost, textAttempted.length, textNow.length)
    ) {
      const { accessId, fieldId, cellId } = fieldContentLogIds(fieldPath);
      const entry = {
        side: "client",
        committed: false,
        cause,
        docId,
        userId,
        sectionId,
        path: key,
        accessId,
        fieldId,
        cellId,
        charsLost,
        charsBefore: textAttempted.length,
        charsAfter: textNow.length,
        charsBeforeSubmit: textBeforeSubmit.length,
        fieldEmptied: textAttempted.length > 0 && textNow.length === 0,
        preview: textAttempted.startsWith(textNow)
          ? textAttempted.slice(textNow.length, textNow.length + 200)
          : textAttempted.slice(0, 200),
        errorMessage,
      };
      losses.push(entry);

      rollbarAndLogWarning("Worksheet field text lost (client, uncommitted)", entry);
      logInfoWithDebugFlag(
        WaffleFlags.DEBUG_WORKSHEET_FIELD_CONTENT,
        "Worksheet field text lost (client, uncommitted)",
        entry,
      );
    }
  }

  return losses;
};

export const reportUncommittedWorksheetFieldTextLoss = (params: {
  docId: string;
  doc: WorksheetType;
  operationId: string | undefined;
  /** Operation ids composed into `operationId` (see submission tracker `combinedOpIds`). */
  relatedOperationIds?: string[];
  cause: ClientWorksheetFieldContentLossCause;
  userId: string | null | undefined;
  sectionId: string | null | undefined;
  errorMessage?: string;
}): Record<string, unknown>[] => {
  const { docId, doc, operationId, relatedOperationIds, cause, userId, sectionId, errorMessage } =
    params;
  const operationIds = [
    ...new Set([...(operationId ? [operationId] : []), ...(relatedOperationIds ?? [])]),
  ];
  if (operationIds.length === 0) {
    return [];
  }

  return operationIds.flatMap((opId) =>
    reportFieldTextLossForOperationId({
      docId,
      doc,
      operationId: opId,
      cause,
      userId,
      sectionId,
      errorMessage,
    }),
  );
};

export const enrichShareDBFailureLog = (
  basePayload: Record<string, unknown>,
  fieldLosses: Record<string, unknown>[],
): Record<string, unknown> => {
  if (fieldLosses.length === 0) {
    return basePayload;
  }
  return {
    ...basePayload,
    worksheetFieldTextLoss: fieldLosses,
  };
};

const collectUncommittedAnswerOps = (doc: ExtendedDoc): Op[] => {
  const queuedOps: Op[] = doc.pendingOps.flatMap((pendingOp) => flattenOps([pendingOp]));
  if (doc.inflightOp) {
    queuedOps.push(...flattenOps([doc.inflightOp]));
  }
  return queuedOps;
};

/** Warn when a doc refresh will drop queued answer-data ops that may contain unsaved text. */
export const logUncommittedOpsDiscardedByDocRefresh = (
  doc: ExtendedDoc,
  trigger: "forceRefreshDoc",
): void => {
  const uncommittedOps = collectUncommittedAnswerOps(doc);
  if (uncommittedOps.length === 0 || !opsTouchAnswerData(uncommittedOps)) {
    return;
  }

  const contentPaths = getAffectedAnswerFieldPaths(uncommittedOps);
  if (contentPaths.length === 0) {
    return;
  }

  const fieldTexts = doc.data ? plainTextsForFieldPaths(doc.data, contentPaths) : {};

  const entry = {
    side: "client",
    committed: false,
    cause: "doc-refresh",
    trigger,
    docId: doc.id,
    pendingOpCount: doc.pendingOps.length,
    hasInflightOp: !!doc.inflightOp,
    uncommittedOpCount: uncommittedOps.length,
    affectedPaths: contentPaths.map(fieldPathKey),
    fieldCharsAtRefresh: Object.fromEntries(
      Object.entries(fieldTexts).map(([path, text]) => [path, text.length]),
    ),
  };

  rollbarAndLogWarning("Worksheet doc refresh discarding uncommitted answer ops", entry);
  logInfoWithDebugFlag(
    WaffleFlags.DEBUG_WORKSHEET_FIELD_CONTENT,
    "Worksheet doc refresh discarding uncommitted answer ops",
    entry,
  );
};
